// frontend/src/components/regulatory/SourcesPanel.tsx
// Shown only when canManageRegulatory(role) — legal_advisor + admin.
// Lists regulatory sources, grouped by country (or filtered by the page's
// countryFilter). Each row: name, url (link), type, active state, Edit,
// Pause/Resume, Delete (soft-delete with confirm). An "Add source" inline
// form with URL + name validation, country select, type select, and
// scope toggle (country-wide vs specific entities — migration 0017).
import * as Dialog from "@radix-ui/react-dialog";
import { useState, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { ExternalLink, Pencil, Trash2, Plus, ChevronDown, ChevronUp, Building2, CheckCircle2, XCircle, PauseCircle, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/cn";
import { relativeTime } from "../../lib/relativeTime";
import {
  fetchRegulatorySourcesForCountry,
  fetchEntities,
  createSource,
  updateSource,
  deleteRegulatorySource,
} from "../../lib/dataFetchers";
import type { RegulatorySource, Country, Entity } from "../../lib/types";

// ─── Source type options ───────────────────────────────────────────────────────

const SOURCE_TYPES = ["registry", "gazette", "regulator", "other"] as const;
type SourceType = (typeof SOURCE_TYPES)[number];

// ─── URL validation ──────────────────────────────────────────────────────────

function isValidUrl(val: string): boolean {
  try {
    const u = new URL(val.trim());
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

// ─── Scope toggle ─────────────────────────────────────────────────────────────

type SourceScope = "country" | "entities";

// ─── Add-source form values ────────────────────────────────────────────────────

interface AddFormValues {
  countryId: string;
  name: string;
  url: string;
  type: SourceType;
  /** "country" = country-wide (legacy); "entities" = entity-scoped (migration 0017) */
  scope: SourceScope;
  /** Selected entity ids when scope="entities". */
  entityIds: string[];
}

interface AddFormErrors {
  countryId?: string;
  name?: string;
  url?: string;
  entityIds?: string;
}

// ─── Edit-source form values ──────────────────────────────────────────────────

interface EditFormValues {
  name: string;
  url: string;
  type: SourceType;
  /** Mirrors AddFormValues — supports re-scoping an existing source. */
  scope: SourceScope;
  entityIds: string[];
}

interface EditFormErrors {
  name?: string;
  url?: string;
  entityIds?: string;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface SourcesPanelProps {
  /** Country iso2 from the page's filter (empty string = all). */
  countryFilterIso2?: string;
  /** All countries for the Add source country selector. */
  countries: Country[];
  /** Map from countryId to Country for display. */
  countryMap: Map<string, Country>;
}

// ─── Edit-source dialog ───────────────────────────────────────────────────────

interface EditDialogProps {
  source: RegulatorySource;
  open: boolean;
  onClose: () => void;
}

function EditSourceDialog({ source, open, onClose }: EditDialogProps): JSX.Element {
  const { t } = useTranslation("regulatory");
  const queryClient = useQueryClient();

  const [form, setForm] = useState<EditFormValues>({
    name: source.name,
    url: source.url,
    type: source.type as SourceType,
    scope: (source.entityIds && source.entityIds.length > 0) ? "entities" : "country",
    entityIds: source.entityIds ?? [],
  });
  const [errors, setErrors] = useState<EditFormErrors>({});

  // Fetch entities for the source's country so the multi-select can render
  // names. Only run when the user has toggled to entity-scope.
  const { data: entitiesData } = useQuery({
    queryKey: ["entities", { countryId: source.countryId, limit: 200 }],
    queryFn: () => fetchEntities({ countryId: source.countryId, limit: 200 }),
    enabled: open && form.scope === "entities",
    staleTime: 300_000,
  });
  const entitiesForCountry = entitiesData?.entities ?? [];

  const mutation = useMutation({
    mutationFn: () =>
      updateSource(source.id, {
        name: form.name.trim(),
        url: form.url.trim(),
        type: form.type,
        // entityIds semantics (per SourceUpdateBody): null = revert/no-change-to-country;
        // [...] = update to entity scope. We always send explicit value here so the
        // backend can detect intent.
        entityIds: form.scope === "entities" && form.entityIds.length > 0
          ? form.entityIds
          : null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["regulatory-sources"] });
      toast.success(t("source_edit_success"));
      onClose();
    },
    onError: () => {
      toast.error(t("generic_error"));
    },
  });

  function validate(): EditFormErrors {
    const errs: EditFormErrors = {};
    if (!form.name.trim()) errs.name = t("source_name_required");
    if (!form.url.trim()) {
      errs.url = t("source_url_required");
    } else if (!isValidUrl(form.url)) {
      errs.url = t("source_url_invalid");
    }
    if (form.scope === "entities" && form.entityIds.length === 0) {
      errs.entityIds = t("source_entities_required");
    }
    return errs;
  }

  function handleSubmit() {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    mutation.mutate();
  }

  function handleScopeChange(scope: SourceScope) {
    setForm((f) => ({ ...f, scope, entityIds: scope === "country" ? [] : f.entityIds }));
    setErrors((e) => ({ ...e, entityIds: undefined }));
  }

  const canSave =
    form.name.trim().length > 0 &&
    form.url.trim().length > 0 &&
    isValidUrl(form.url) &&
    (form.scope === "country" || form.entityIds.length > 0) &&
    !mutation.isPending;

  return (
    <Dialog.Root open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40" />
        <Dialog.Content
          className={cn(
            "fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50",
            "bg-surface rounded-xl border border-border shadow-card-hov",
            "p-6 w-[min(92vw,480px)] max-h-[90vh] overflow-y-auto focus:outline-none",
          )}
          aria-describedby="edit-source-desc"
        >
          <Dialog.Title className="text-base font-semibold text-text mb-1">
            {t("source_edit_title")}
          </Dialog.Title>
          <Dialog.Description id="edit-source-desc" className="text-sm text-text-muted mb-4">
            {t("source_edit_desc")}
          </Dialog.Description>

          <div className="space-y-4">
            {/* Name */}
            <div>
              <label htmlFor={`edit-source-name-${source.id}`} className="block text-sm font-medium text-text mb-1">
                {t("source_field_name")} <span className="text-sev-expired" aria-hidden>*</span>
              </label>
              <input
                id={`edit-source-name-${source.id}`}
                type="text"
                maxLength={200}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder={t("source_field_name_placeholder")}
                className={cn(
                  "w-full border rounded-lg px-3 py-2 text-base",
                  "bg-bg text-text placeholder:text-text-dim",
                  "focus:outline-none focus:ring-2 focus:ring-accent/30",
                  "min-h-[44px]",
                  errors.name ? "border-sev-expired" : "border-border",
                )}
              />
              {errors.name && <p className="text-xs text-sev-expired mt-1" role="alert">{errors.name}</p>}
            </div>

            {/* URL */}
            <div>
              <label htmlFor={`edit-source-url-${source.id}`} className="block text-sm font-medium text-text mb-1">
                {t("source_field_url")} <span className="text-sev-expired" aria-hidden>*</span>
              </label>
              <input
                id={`edit-source-url-${source.id}`}
                type="url"
                value={form.url}
                onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                placeholder="https://"
                className={cn(
                  "w-full border rounded-lg px-3 py-2 text-base",
                  "bg-bg text-text placeholder:text-text-dim",
                  "focus:outline-none focus:ring-2 focus:ring-accent/30",
                  "min-h-[44px]",
                  errors.url ? "border-sev-expired" : "border-border",
                )}
              />
              {errors.url && <p className="text-xs text-sev-expired mt-1" role="alert">{errors.url}</p>}
            </div>

            {/* Type */}
            <div>
              <label htmlFor={`edit-source-type-${source.id}`} className="block text-sm font-medium text-text mb-1">
                {t("source_field_type")}
              </label>
              <select
                id={`edit-source-type-${source.id}`}
                value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as SourceType }))}
                className={cn(
                  "w-full border border-border rounded-lg px-3 py-2 text-base",
                  "bg-bg text-text focus:outline-none focus:ring-2 focus:ring-accent/30",
                  "min-h-[44px]",
                )}
              >
                {SOURCE_TYPES.map((st) => (
                  <option key={st} value={st}>{t(`source_type_${st}`)}</option>
                ))}
              </select>
            </div>

            {/* Scope toggle — mirrors Add form so operators can re-scope an
                existing source instead of having to delete + recreate. */}
            <div>
              <p
                className="block text-sm font-medium text-text mb-1.5"
                id={`edit-source-scope-label-${source.id}`}
              >
                {t("source_scope_label")}
              </p>
              <div
                role="radiogroup"
                aria-labelledby={`edit-source-scope-label-${source.id}`}
                className="flex gap-3"
              >
                <label className={cn(
                  "flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer select-none",
                  "text-sm transition-colors min-h-[44px] sm:min-h-[36px]",
                  form.scope === "country"
                    ? "border-accent bg-accent/5 text-accent font-medium"
                    : "border-border bg-surface text-text hover:bg-surface-2",
                )}>
                  <input
                    type="radio"
                    name={`edit-source-scope-${source.id}`}
                    value="country"
                    checked={form.scope === "country"}
                    onChange={() => handleScopeChange("country")}
                    className="sr-only"
                  />
                  {t("source_scope_country")}
                </label>

                <label className={cn(
                  "flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer select-none",
                  "text-sm transition-colors min-h-[44px] sm:min-h-[36px]",
                  form.scope === "entities"
                    ? "border-accent bg-accent/5 text-accent font-medium"
                    : "border-border bg-surface text-text hover:bg-surface-2",
                )}>
                  <input
                    type="radio"
                    name={`edit-source-scope-${source.id}`}
                    value="entities"
                    checked={form.scope === "entities"}
                    onChange={() => handleScopeChange("entities")}
                    className="sr-only"
                  />
                  <Building2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  {t("source_scope_entities")}
                </label>
              </div>
            </div>

            {/* Entity multi-select — shown when scope = "entities" */}
            {form.scope === "entities" && (
              <EntityMultiSelect
                entities={entitiesForCountry}
                selectedIds={form.entityIds}
                onChange={(ids) => {
                  setForm((f) => ({ ...f, entityIds: ids }));
                  setErrors((e) => ({ ...e, entityIds: undefined }));
                }}
                error={errors.entityIds}
                formId={`edit-source-${source.id}`}
              />
            )}
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={onClose}
              className={cn(
                "px-4 py-2 rounded-lg text-sm text-text-muted hover:bg-bg",
                "min-h-[44px] focus:outline-none focus:ring-2 focus:ring-border",
              )}
            >
              {t("cancel_button")}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSave}
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium text-white",
                "bg-accent hover:bg-accent/90",
                "disabled:opacity-40 disabled:cursor-not-allowed",
                "min-h-[44px] focus:outline-none focus:ring-2 focus:ring-accent/30",
              )}
            >
              {mutation.isPending ? "…" : t("source_save_button")}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Delete-confirm dialog ────────────────────────────────────────────────────

interface DeleteDialogProps {
  source: RegulatorySource;
  open: boolean;
  onClose: () => void;
}

function DeleteSourceDialog({ source, open, onClose }: DeleteDialogProps): JSX.Element {
  const { t } = useTranslation("regulatory");
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => deleteRegulatorySource(source.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["regulatory-sources"] });
      toast.success(t("delete_source_success"));
      onClose();
    },
    onError: () => {
      toast.error(t("generic_error"));
    },
  });

  return (
    <Dialog.Root open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40" />
        <Dialog.Content
          className={cn(
            "fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50",
            "bg-surface rounded-xl border border-border shadow-card-hov",
            "p-6 w-[min(92vw,400px)] focus:outline-none",
          )}
          aria-describedby="delete-source-desc"
        >
          <Dialog.Title className="text-base font-semibold text-text mb-1">
            {t("source_delete_title")}
          </Dialog.Title>
          <Dialog.Description id="delete-source-desc" className="text-sm text-text-muted mb-5">
            {t("source_delete_confirm", { name: source.name })}
          </Dialog.Description>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className={cn(
                "px-4 py-2 rounded-lg text-sm text-text-muted hover:bg-bg",
                "min-h-[44px] focus:outline-none focus:ring-2 focus:ring-border",
              )}
            >
              {t("cancel_button")}
            </button>
            <button
              type="button"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium text-white",
                "bg-sev-expired hover:bg-sev-expired/90",
                "disabled:opacity-40 disabled:cursor-not-allowed",
                "min-h-[44px] focus:outline-none focus:ring-2 focus:ring-sev-expired/30",
              )}
            >
              {mutation.isPending ? "…" : t("source_delete_button")}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Source health badge ──────────────────────────────────────────────────────

type SourceHealth = "manual" | "failed" | "watching" | "pending";

/**
 * Derive the health state from the P0 source-health fields:
 *  - Manual  → monitoringMode === "manual"
 *  - Watching → lastFetchStatus === "ok"
 *  - Failed  → lastFetchStatus === "failed" (a real failed fetch)
 *  - Pending → not yet fetched (no lastFetchStatus). A never-checked source must
 *    NOT read as "Failed" — it just hasn't been checked by a run yet.
 * Manual takes precedence.
 */
function sourceHealth(source: RegulatorySource): SourceHealth {
  if (source.monitoringMode === "manual") return "manual";
  if (source.lastFetchStatus === "ok") return "watching";
  if (source.lastFetchStatus === "failed") return "failed";
  return "pending";
}

function SourceStatusBadge({ source }: { source: RegulatorySource }): JSX.Element {
  const { t } = useTranslation("regulatory");
  const health = sourceHealth(source);

  const config: Record<SourceHealth, { label: string; cls: string; Icon: typeof CheckCircle2 }> = {
    watching: {
      label: t("source_status_watching"),
      cls: "bg-[#f0fdf4] text-[#15803d] border-[#bbf7d0]",
      Icon: CheckCircle2,
    },
    failed: {
      label: t("source_status_failed"),
      cls: "bg-[#fff1f2] text-sev-expired border-[#fecdd3]",
      Icon: XCircle,
    },
    manual: {
      label: t("source_status_manual"),
      cls: "bg-[#f1f5f9] text-[#64748b] border-[#cbd5e1]",
      Icon: PauseCircle,
    },
    pending: {
      label: t("source_status_pending"),
      cls: "bg-[#f1f5f9] text-[#64748b] border-[#cbd5e1]",
      Icon: Clock,
    },
  };

  const { label, cls, Icon } = config[health];

  return (
    <span
      data-testid="source-status-badge"
      data-health={health}
      className={cn(
        "inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border shrink-0",
        cls,
      )}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {label}
    </span>
  );
}

// ─── Source row ───────────────────────────────────────────────────────────────

interface SourceRowProps {
  source: RegulatorySource;
  /** Entity name lookup for entity-scoped sources. */
  entityNames?: Map<string, string>;
}

function SourceRow({ source, entityNames }: SourceRowProps): JSX.Element {
  const { t } = useTranslation("regulatory");

  const isEntityScoped = (source.entityIds?.length ?? 0) > 0;

  const lastReadLabel = source.lastSuccessfulReadAt
    ? t("source_last_read", { when: relativeTime(source.lastSuccessfulReadAt) })
    : t("source_never_read");

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  return (
    <>
      <li
        data-testid="source-row"
        data-source-id={source.id}
        className={cn(
          "flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5 px-3 rounded-[9px]",
          "border border-border-2 bg-surface hover:bg-surface-2 text-sm transition-colors",
        )}
      >
        {/* Type badge */}
        <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-border text-text-dim shrink-0">
          {t(`source_type_${source.type}`)}
        </span>

        {/* Health status badge (P0) */}
        <SourceStatusBadge source={source} />

        {/* Entity-scoped badge (migration 0017) */}
        {isEntityScoped && (
          <span
            className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-accent/10 text-accent shrink-0"
            title={source.entityIds!.map((id) => entityNames?.get(id) ?? id).join(", ")}
          >
            {source.entityIds!.length === 1
              ? t("source_entity_count", { count: 1 })
              : t("source_entity_count_plural", { count: source.entityIds!.length })}
          </span>
        )}

        {/* Name */}
        <span className="font-medium text-text truncate flex-1 min-w-0">
          {source.name}
        </span>

        {/* URL link */}
        <a
          href={source.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${t("source_open_link")} — ${source.name}`}
          className={cn(
            "inline-flex items-center gap-1 text-xs text-accent hover:underline shrink-0",
            "focus:outline-none focus:ring-1 focus:ring-accent/30 rounded",
          )}
        >
          <ExternalLink className="h-3 w-3" aria-hidden />
          {t("source_open_link")}
        </a>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0 ml-auto">
          {/* Edit */}
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            aria-label={`${t("source_edit_button")} — ${source.name}`}
            className={cn(
              "p-1.5 rounded hover:bg-border text-text-dim hover:text-text",
              "min-h-[44px] min-w-[44px] flex items-center justify-center",
              "focus:outline-none focus:ring-1 focus:ring-accent/30",
            )}
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden />
          </button>

          {/* Delete */}
          <button
            type="button"
            onClick={() => setDeleteOpen(true)}
            aria-label={`${t("delete_source_button")} — ${source.name}`}
            className={cn(
              "p-1.5 rounded hover:bg-[#fff1f2] text-text-dim hover:text-sev-expired",
              "min-h-[44px] min-w-[44px] flex items-center justify-center",
              "focus:outline-none focus:ring-1 focus:ring-sev-expired/30",
            )}
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>

        {/* Last successfully read — distinct from lastCheckedAt; full-width line */}
        <p
          data-testid="source-last-read"
          className="basis-full text-[11px] text-text-dim"
        >
          {lastReadLabel}
        </p>
      </li>

      {editOpen && (
        <EditSourceDialog key={`${source.id}-${source.updatedAt}`} source={source} open={editOpen} onClose={() => setEditOpen(false)} />
      )}
      {deleteOpen && (
        <DeleteSourceDialog source={source} open={deleteOpen} onClose={() => setDeleteOpen(false)} />
      )}
    </>
  );
}

// ─── Entity multi-select checkbox list ────────────────────────────────────────

interface EntityMultiSelectProps {
  entities: Entity[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  error?: string;
  formId: string;
}

function EntityMultiSelect({ entities, selectedIds, onChange, error, formId }: EntityMultiSelectProps): JSX.Element {
  const { t } = useTranslation("regulatory");
  const labelId = `${formId}-entities-label`;
  const errorId = `${formId}-entities-error`;

  function toggle(id: string) {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((x) => x !== id)
        : [...selectedIds, id],
    );
  }

  if (entities.length === 0) {
    return (
      <p className="text-sm text-dim italic py-2">
        {t("source_entities_placeholder")}
      </p>
    );
  }

  return (
    <div>
      <p
        id={labelId}
        className="block text-sm font-medium text-text mb-1"
      >
        {t("source_entities_label")} <span className="text-sev-expired" aria-hidden>*</span>
      </p>
      <div
        role="group"
        aria-labelledby={labelId}
        aria-describedby={error ? errorId : undefined}
        className={cn(
          "rounded-lg border px-3 py-2 max-h-48 overflow-y-auto space-y-1",
          "bg-surface",
          error ? "border-sev-expired" : "border-border",
        )}
      >
        {entities.map((entity) => {
          const checked = selectedIds.includes(entity.id);
          const checkId = `${formId}-entity-${entity.id}`;
          return (
            <label
              key={entity.id}
              htmlFor={checkId}
              className={cn(
                "flex items-center gap-2.5 rounded px-1 py-1.5 cursor-pointer select-none",
                "text-sm text-text hover:bg-surface-2",
                "min-h-[44px] sm:min-h-0",
              )}
            >
              <input
                id={checkId}
                type="checkbox"
                checked={checked}
                onChange={() => toggle(entity.id)}
                className="h-4 w-4 rounded border-border accent-accent shrink-0"
              />
              <span className="truncate">{entity.name}</span>
              {entity.legalForm && (
                <span className="text-dim text-xs shrink-0">{entity.legalForm}</span>
              )}
            </label>
          );
        })}
      </div>
      {selectedIds.length > 0 && (
        <p className="text-xs text-text-muted mt-1">
          {selectedIds.length === 1
            ? t("source_entity_count", { count: selectedIds.length })
            : t("source_entity_count_plural", { count: selectedIds.length })}
        </p>
      )}
      {error && (
        <p id={errorId} className="text-xs text-sev-expired mt-1" role="alert">{error}</p>
      )}
    </div>
  );
}

// ─── Add-source inline form ───────────────────────────────────────────────────

interface AddSourceFormProps {
  countries: Country[];
  /** Pre-selected countryId when the page is filtered to a single country. */
  preselectedCountryId?: string;
  onCancel: () => void;
}

function AddSourceForm({ countries, preselectedCountryId, onCancel }: AddSourceFormProps): JSX.Element {
  const { t } = useTranslation("regulatory");
  const queryClient = useQueryClient();

  const [form, setForm] = useState<AddFormValues>({
    countryId: preselectedCountryId ?? "",
    name: "",
    url: "",
    type: "other",
    scope: "country",
    entityIds: [],
  });
  const [errors, setErrors] = useState<AddFormErrors>({});

  // Fetch entities for the selected country when scope = "entities"
  const { data: entitiesData } = useQuery({
    queryKey: ["entities", { countryId: form.countryId, limit: 200 }],
    queryFn: () => fetchEntities({ countryId: form.countryId, limit: 200 }),
    enabled: form.scope === "entities" && form.countryId.length > 0,
    staleTime: 300_000,
  });
  const entitiesForCountry = entitiesData?.entities ?? [];

  const mutation = useMutation({
    mutationFn: () =>
      createSource({
        countryId: form.countryId,
        name: form.name.trim(),
        url: form.url.trim(),
        type: form.type,
        active: true,
        // Only include entityIds when scope is "entities" and at least one selected
        entityIds: form.scope === "entities" && form.entityIds.length > 0
          ? form.entityIds
          : null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["regulatory-sources"] });
      toast.success(t("source_add_success"));
      onCancel();
    },
    onError: () => {
      toast.error(t("generic_error"));
    },
  });

  function validate(): AddFormErrors {
    const errs: AddFormErrors = {};
    if (!form.countryId) errs.countryId = t("source_country_required");
    if (!form.name.trim()) errs.name = t("source_name_required");
    if (!form.url.trim()) {
      errs.url = t("source_url_required");
    } else if (!isValidUrl(form.url)) {
      errs.url = t("source_url_invalid");
    }
    if (form.scope === "entities" && form.entityIds.length === 0) {
      errs.entityIds = t("source_entities_required");
    }
    return errs;
  }

  function handleSubmit() {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    mutation.mutate();
  }

  function handleScopeChange(scope: SourceScope) {
    setForm((f) => ({ ...f, scope, entityIds: [] }));
    setErrors((e) => ({ ...e, entityIds: undefined }));
  }

  function handleCountryChange(countryId: string) {
    // Reset entity selection when country changes
    setForm((f) => ({ ...f, countryId, entityIds: [] }));
    setErrors((e) => ({ ...e, countryId: undefined, entityIds: undefined }));
  }

  const canAdd =
    form.countryId.length > 0 &&
    form.name.trim().length > 0 &&
    form.url.trim().length > 0 &&
    isValidUrl(form.url) &&
    (form.scope === "country" || form.entityIds.length > 0) &&
    !mutation.isPending;

  return (
    <div
      data-testid="add-source-form"
      className="rounded-lg border border-accent/30 bg-bg p-4 space-y-4"
      role="form"
      aria-label={t("source_add_title")}
    >
      <p className="text-sm font-medium text-text">{t("source_add_title")}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Country */}
        <div className={cn(preselectedCountryId && "sm:col-span-2")}>
          <label htmlFor="add-source-country" className="block text-sm font-medium text-text mb-1">
            {t("source_field_country")} <span className="text-sev-expired" aria-hidden>*</span>
          </label>
          {preselectedCountryId ? (
            <p className="text-sm text-text-muted py-2">
              {countries.find((c) => c.id === preselectedCountryId)?.name ?? preselectedCountryId}
            </p>
          ) : (
            <select
              id="add-source-country"
              value={form.countryId}
              onChange={(e) => handleCountryChange(e.target.value)}
              className={cn(
                "w-full border rounded-lg px-3 py-2 text-base",
                "bg-surface text-text focus:outline-none focus:ring-2 focus:ring-accent/30",
                "min-h-[44px]",
                errors.countryId ? "border-sev-expired" : "border-border",
              )}
            >
              <option value="">{t("source_country_placeholder")}</option>
              {[...countries]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.flag ?? ""} {c.name}
                  </option>
                ))}
            </select>
          )}
          {errors.countryId && <p className="text-xs text-sev-expired mt-1" role="alert">{errors.countryId}</p>}
        </div>

        {/* Name */}
        <div>
          <label htmlFor="add-source-name" className="block text-sm font-medium text-text mb-1">
            {t("source_field_name")} <span className="text-sev-expired" aria-hidden>*</span>
          </label>
          <input
            id="add-source-name"
            type="text"
            maxLength={200}
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder={t("source_field_name_placeholder")}
            className={cn(
              "w-full border rounded-lg px-3 py-2 text-base",
              "bg-surface text-text placeholder:text-text-dim",
              "focus:outline-none focus:ring-2 focus:ring-accent/30",
              "min-h-[44px]",
              errors.name ? "border-sev-expired" : "border-border",
            )}
          />
          {errors.name && <p className="text-xs text-sev-expired mt-1" role="alert">{errors.name}</p>}
        </div>

        {/* Type */}
        <div>
          <label htmlFor="add-source-type" className="block text-sm font-medium text-text mb-1">
            {t("source_field_type")}
          </label>
          <select
            id="add-source-type"
            value={form.type}
            onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as SourceType }))}
            className={cn(
              "w-full border border-border rounded-lg px-3 py-2 text-base",
              "bg-surface text-text focus:outline-none focus:ring-2 focus:ring-accent/30",
              "min-h-[44px]",
            )}
          >
            {SOURCE_TYPES.map((st) => (
              <option key={st} value={st}>{t(`source_type_${st}`)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* URL — full width */}
      <div>
        <label htmlFor="add-source-url" className="block text-sm font-medium text-text mb-1">
          {t("source_field_url")} <span className="text-sev-expired" aria-hidden>*</span>
        </label>
        <input
          id="add-source-url"
          type="url"
          value={form.url}
          onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
          placeholder="https://"
          className={cn(
            "w-full border rounded-lg px-3 py-2 text-base",
            "bg-surface text-text placeholder:text-text-dim",
            "focus:outline-none focus:ring-2 focus:ring-accent/30",
            "min-h-[44px]",
            errors.url ? "border-sev-expired" : "border-border",
          )}
        />
        {errors.url && <p className="text-xs text-sev-expired mt-1" role="alert">{errors.url}</p>}
      </div>

      {/* Scope toggle — Country-wide vs Specific entities (migration 0017) */}
      <div>
        <p className="block text-sm font-medium text-text mb-1.5" id="add-source-scope-label">
          {t("source_scope_label")}
        </p>
        <div
          role="radiogroup"
          aria-labelledby="add-source-scope-label"
          className="flex gap-3"
        >
          <label className={cn(
            "flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer select-none",
            "text-sm transition-colors min-h-[44px] sm:min-h-[36px]",
            form.scope === "country"
              ? "border-accent bg-accent/5 text-accent font-medium"
              : "border-border bg-surface text-text hover:bg-surface-2",
          )}>
            <input
              type="radio"
              name="add-source-scope"
              value="country"
              checked={form.scope === "country"}
              onChange={() => handleScopeChange("country")}
              className="sr-only"
            />
            {t("source_scope_country")}
          </label>

          <label className={cn(
            "flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer select-none",
            "text-sm transition-colors min-h-[44px] sm:min-h-[36px]",
            form.scope === "entities"
              ? "border-accent bg-accent/5 text-accent font-medium"
              : "border-border bg-surface text-text hover:bg-surface-2",
          )}>
            <input
              type="radio"
              name="add-source-scope"
              value="entities"
              checked={form.scope === "entities"}
              onChange={() => handleScopeChange("entities")}
              className="sr-only"
            />
            <Building2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {t("source_scope_entities")}
          </label>
        </div>
      </div>

      {/* Entity multi-select — shown when scope = "entities" */}
      {form.scope === "entities" && (
        <div>
          {!form.countryId ? (
            <p className="text-sm text-dim italic">
              {t("source_country_placeholder")}
            </p>
          ) : (
            <EntityMultiSelect
              entities={entitiesForCountry}
              selectedIds={form.entityIds}
              onChange={(ids) => {
                setForm((f) => ({ ...f, entityIds: ids }));
                setErrors((e) => ({ ...e, entityIds: undefined }));
              }}
              error={errors.entityIds}
              formId="add-source"
            />
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className={cn(
            "px-4 py-2 rounded-lg text-sm text-text-muted hover:bg-border",
            "min-h-[44px] focus:outline-none focus:ring-2 focus:ring-border",
          )}
        >
          {t("cancel_button")}
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canAdd}
          data-testid="add-source-submit"
          className={cn(
            "px-4 py-2 rounded-lg text-sm font-medium text-white",
            "bg-accent hover:bg-accent/90",
            "disabled:opacity-40 disabled:cursor-not-allowed",
            "min-h-[44px] focus:outline-none focus:ring-2 focus:ring-accent/30",
          )}
        >
          {mutation.isPending ? "…" : t("source_add_button")}
        </button>
      </div>
    </div>
  );
}

// ─── Main panel ───────────────────────────────────────────────────────────────

export function SourcesPanel({ countryFilterIso2, countries, countryMap }: SourcesPanelProps): JSX.Element {
  const { t } = useTranslation("regulatory");

  const [expanded, setExpanded] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  const { data: sources = [], isLoading, isError } = useQuery({
    queryKey: ["regulatory-sources", { country: countryFilterIso2 ?? null, includeInactive: true }],
    // Managers intentionally see paused sources — pass includeInactive=true explicitly
    // (deliberate deviation from fetchRegulatorySourcesForCountry's default of false).
    queryFn: () => fetchRegulatorySourcesForCountry(countryFilterIso2 || undefined, true),
    staleTime: 120_000,
  });

  // Fetch all entities (limit 500) to resolve entity names on entity-scoped source rows.
  // Stale time is long — entity list changes infrequently.
  const { data: allEntitiesData } = useQuery({
    queryKey: ["entities-all-for-sources"],
    queryFn: () => fetchEntities({ limit: 500 }),
    staleTime: 300_000,
  });

  const entityNamesMap = useMemo<Map<string, string>>(() => {
    const map = new Map<string, string>();
    for (const e of allEntitiesData?.entities ?? []) {
      map.set(e.id, e.name);
    }
    return map;
  }, [allEntitiesData]);

  // Resolve the countryId for "preselected" when a single country is filtered
  const preselectedCountryId = useMemo((): string | undefined => {
    if (!countryFilterIso2) return undefined;
    for (const [id, c] of countryMap.entries()) {
      if (c.iso2 === countryFilterIso2) return id;
    }
    return undefined;
  }, [countryFilterIso2, countryMap]);

  // Group sources by countryId
  const grouped = (() => {
    const map = new Map<string, RegulatorySource[]>();
    for (const s of sources) {
      const list = map.get(s.countryId) ?? [];
      list.push(s);
      map.set(s.countryId, list);
    }
    return Array.from(map.entries())
      .map(([cid, list]) => ({ cid, list }))
      .sort((a, b) => {
        const nameA = countryMap.get(a.cid)?.name ?? a.cid;
        const nameB = countryMap.get(b.cid)?.name ?? b.cid;
        return nameA.localeCompare(nameB);
      });
  })();

  const totalCount = sources.length;
  const activeCount = sources.filter((s) => s.active).length;

  return (
    <section
      data-testid="sources-panel"
      className="bg-surface border border-border rounded-[11px] shadow-card overflow-hidden mb-4"
      aria-labelledby="sources-panel-heading"
    >
      {/* Panel header — always visible (.card-h style) */}
      <div className="flex items-center justify-between gap-3 flex-wrap px-[15px] py-[12px] border-b border-border-2">
        <div className="flex items-center gap-2">
          <h2
            id="sources-panel-heading"
            className="font-display font-semibold text-[15px] tracking-[-0.01em] text-text"
          >
            {t("sources_panel_title")}
          </h2>
          {!isLoading && (
            <span className="font-sans text-[12px] text-muted font-normal ml-1">
              {t("sources_panel_count", { active: activeCount, total: totalCount })}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Add source */}
          {!showAddForm && (
            <button
              type="button"
              onClick={() => { setShowAddForm(true); setExpanded(true); }}
              data-testid="add-source-button"
              className={cn(
                "inline-flex items-center gap-1.5 h-[34px] px-3 rounded-lg text-[12.5px] font-semibold border border-transparent",
                "bg-accent text-white hover:bg-[#4338ca]",
                "min-h-[44px] sm:min-h-[34px] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/20",
              )}
            >
              <Plus className="h-3.5 w-3.5" aria-hidden />
              {t("source_add_button")}
            </button>
          )}

          {/* Expand/collapse */}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-controls="sources-panel-body"
            aria-label={expanded ? t("sources_panel_collapse") : t("sources_panel_expand")}
            className={cn(
              "p-1.5 rounded-lg hover:bg-surface-2 text-dim",
              "min-h-[44px] sm:min-h-[34px] min-w-[34px] flex items-center justify-center",
              "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/20",
            )}
          >
            {expanded
              ? <ChevronUp className="h-4 w-4" aria-hidden />
              : <ChevronDown className="h-4 w-4" aria-hidden />}
          </button>
        </div>
      </div>

      {/* Collapsible body */}
      {expanded && (
        <div id="sources-panel-body" className="px-[15px] py-[9px] space-y-4">
          {/* Add source inline form */}
          {showAddForm && (
            <AddSourceForm
              countries={countries}
              preselectedCountryId={preselectedCountryId}
              onCancel={() => setShowAddForm(false)}
            />
          )}

          {/* Loading / error / empty states */}
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="skeleton h-12 rounded-lg" />
              ))}
            </div>
          ) : isError ? (
            <p className="text-sm text-text-muted py-2">{t("generic_error")}</p>
          ) : grouped.length === 0 ? (
            <p className="text-sm text-text-dim py-2 italic">{t("sources_panel_empty")}</p>
          ) : (
            <div className="space-y-5">
              {grouped.map(({ cid, list }) => {
                const country = countryMap.get(cid);
                const countryLabel = country
                  ? `${country.flag ?? ""} ${country.name}`
                  : cid;

                return (
                  <section key={cid} aria-labelledby={`sources-country-${cid}`}>
                    <h3
                      id={`sources-country-${cid}`}
                      className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-2"
                    >
                      {countryLabel}
                    </h3>
                    <ul className="space-y-2">
                      {list.map((source) => (
                        <SourceRow key={source.id} source={source} entityNames={entityNamesMap} />
                      ))}
                    </ul>
                  </section>
                );
              })}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
